/* eslint-disable react-hooks/exhaustive-deps */
import { Grid, makeStyles, Typography } from '@material-ui/core';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import i18n from 'i18n';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import { parseNameLdap, cyrb53Hash } from 'utils/helpers';
import {
  UserPartnersFields,
  UserPartnersListProps,
} from 'modules/users/interfaces';
import { getPartnersByUserUuidRequest } from 'modules/users/actions';
import { useTableFilters } from 'hooks/useTableFilters';
import { SearchBar } from 'components/SearchBar';
import styles from '../styles';

const useStyles = makeStyles(styles);

const initialQuery = {
  offset: 0,
  limit: 10,
  search: '',
};

interface Props {
  userUuid: string;
  userPartnersList: UserPartnersListProps;
  isLoadingUserPartnersList: boolean;
  getPartnersByUserUuidRequest: (props) => void;
}

const PARTNER_STATUS_I18N: Record<string, string> = {
  active: 'ACTIVE_KEY',
  inactive: 'INACTIVE_KEY',
  suspended: 'PARTNER_PAGE.SUSPENDED_KEY',
};

const formatYesNo = (value: boolean | null | undefined): string => {
  if (value == null) return '-';
  return value ? i18n.t('YES_KEY') : i18n.t('NO_KEY');
};

const ListPartners = ({
  userUuid,
  userPartnersList,
  isLoadingUserPartnersList,
  getPartnersByUserUuidRequest,
}: Props) => {
  const classes = useStyles();

  const {
    query,
    itemOrder,
    handleOrder,
    searchValue,
    handleSearch,
    setSearchValue,
    handleLimitChange,
    handlePageChange,
  } = useTableFilters(initialQuery);

  const headers = [
    {
      key: 'company_name',
      i18n: 'OPERATOR_VIEW_COMPANY_NAME_KEY',
      order: true,
    },
    {
      key: 'name',
      i18n: 'OPERATOR_VIEW_PARTNER_NAME_KEY',
      order: true,
    },
    {
      key: 'partner_status',
      i18n: 'STATUS_KEY',
      centerTitle: true,
      order: false,
    },
    {
      key: 'ldap_type',
      i18n: 'DYNAMIC_FILTER.LDAP_TYPE_KEY',
      centerTitle: true,
      order: true,
    },
    {
      key: 'can_external_ad_change_password',
      i18n: 'ALLOW_CHANGE_PASSWORD_KEY',
      centerTitle: true,
      order: true,
    },
    {
      key: 'allow_sso_configuration',
      i18n: 'PARTNER_PAGE.SSO_KEY',
      centerTitle: true,
      order: true,
    },
  ];

  const renderPartnerStatus = (status: string) => {
    const key = PARTNER_STATUS_I18N[status];
    return key ? i18n.t(key) : status;
  };

  const getPartners = () => {
    getPartnersByUserUuidRequest({
      userUuid,
      query,
    });
  };

  React.useEffect(() => {
    getPartners();
  }, [query, userUuid]);

  return (
    <Grid container>
      <Grid container>
        <SearchBar
          id="search-partners"
          isLoading={isLoadingUserPartnersList}
          searchValue={searchValue}
          placeholder={i18n.t('SEARCH_KEY')}
          handleSearch={handleSearch}
          setSearchValue={setSearchValue}
        />
        <TableItems
          header={
            <>
              {headers.map(item => (
                <TableCell
                  centerTitle={item?.centerTitle}
                  key={item?.key}
                  order={item?.order}
                  markOderItem={itemOrder === item?.key}
                  handleOrder={() =>
                    item?.order ? handleOrder(item?.key) : null
                  }
                >
                  {i18n.t(item?.i18n)}
                </TableCell>
              ))}
            </>
          }
          renderItems={(item: UserPartnersFields, index: number) => (
            <TableRow key={cyrb53Hash(String(index))}>
              <TableCell copyable className={classes.textField}>
                {item?.company_name ?? '-'}
              </TableCell>
              <TableCell copyable className={classes.textField}>
                {item?.name ?? '-'}
              </TableCell>
              <TableCell className={classes.textFieldCenter}>
                <Typography className={classes.textFieldCenterText}>
                  {item?.partner_status
                    ? renderPartnerStatus(item.partner_status)
                    : '-'}
                </Typography>
              </TableCell>
              <TableCell className={classes.textFieldCenter}>
                <Typography className={classes.textFieldCenterText}>
                  {item?.active_directory?.ldap_type
                    ? parseNameLdap(item.active_directory.ldap_type)
                    : '-'}
                </Typography>
              </TableCell>
              <TableCell className={classes.textFieldCenter}>
                <Typography className={classes.textFieldCenterText}>
                  {formatYesNo(
                    item?.active_directory?.can_external_ad_change_password
                  )}
                </Typography>
              </TableCell>
              <TableCell className={classes.textFieldCenter}>
                <Typography className={classes.textFieldCenterText}>
                  {formatYesNo(item?.allow_sso_configuration)}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          data={userPartnersList}
          isLoading={isLoadingUserPartnersList}
          onChangePage={handlePageChange}
          onChangeLimit={handleLimitChange}
          notFoundText={i18n.t('LOGS_PAGE.NO_LOGS_KEY')}
        />
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  users: { userPartnersList, isLoadingUserPartnersList },
}: ApplicationState) => ({
  userPartnersList,
  isLoadingUserPartnersList,
});

export default connect(mapStateToProps, {
  getPartnersByUserUuidRequest,
})(ListPartners);
